import { useCallback, useEffect } from 'react';
import { css } from '@emotion/react';
import { CalloutDialog } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { FeatureId, useEligibility } from '@frontend/user-preferences';
import { Button, Text, usePopoverDialog } from '@frontend/design-system';
import { HISTORY_TRAY_WIDTH } from '../phone-system-history/history-details-tray/history-details-tray';
import { SeeHistoryButton } from './see-history-button';

type SeeHistoryButtonWithCalloutProps = {
  featureId: FeatureId;
  onClick: () => void;
  isActive?: boolean;
  /**
   * ISO date string (YYYY-MM-DD) for when the callout becomes visible.
   * Set to the day the "See History" button was added for this page.
   */
  startDate?: string;
  /**
   * ISO date string (YYYY-MM-DD) for when the callout stops showing.
   * Typically 2 months after the button was introduced (e.g., the feature's GA date).
   */
  endDate?: string;
  trackingIdPrefix?: string;
  /**
   * Custom label to display instead of the default "See History"/"Hide History" text.
   */
  label?: string;
};

export const SeeHistoryButtonWithCallout = ({
  featureId,
  onClick,
  isActive,
  startDate,
  endDate,
  trackingIdPrefix,
  label,
}: SeeHistoryButtonWithCalloutProps) => {
  const { t } = useTranslation('phone');

  const { shouldShow, disableEligibility } = useEligibility({
    featureId,
    defaultCriteria:
      startDate && endDate
        ? {
            strategy: 'any',
            conditions: [
              { type: 'days-shown', maxDaysToShow: 2 },
              { type: 'date-range', startDate, endDate },
            ],
          }
        : { type: 'days-shown', maxDaysToShow: 2 },
  });

  const {
    getDialogProps,
    close: closeCalloutDialog,
    open: openCalloutDialog,
    refs,
  } = usePopoverDialog({
    placement: 'left',
    interactionOptions: {
      dismiss: {
        outsidePress: false,
        escapeKey: false,
      },
    },
  });

  useEffect(() => {
    if (shouldShow && !isActive) {
      openCalloutDialog();
    } else {
      closeCalloutDialog();
    }
  }, [shouldShow, isActive, openCalloutDialog, closeCalloutDialog]);

  const handleSeeHistoryClick = useCallback(() => {
    if (shouldShow) {
      disableEligibility();
    }
    onClick();
  }, [disableEligibility, onClick, shouldShow]);

  const handleGotItClick = useCallback(() => {
    disableEligibility();
    closeCalloutDialog();
  }, [disableEligibility, closeCalloutDialog]);

  return (
    <div
      ref={refs.setReference}
      css={css`
        // Make the button a fixed distance from the right edge of the screen for large screens so that it does not appear
        // like it is floating in the middle of the page. When the history tray is open, move the button to the left so
        // that it does not get covered by the tray.
        @media (min-width: 1660px) {
          position: absolute;
          right: ${isActive ? `${HISTORY_TRAY_WIDTH + 32}px` : '80px'};
        }
      `}
    >
      <SeeHistoryButton
        onClick={handleSeeHistoryClick}
        isActive={isActive}
        trackingId={trackingIdPrefix ? `${trackingIdPrefix}-see-history-button` : undefined}
        label={label}
      />

      <CalloutDialog
        color='seaweed'
        popoverDialogProps={getDialogProps()}
        badge='new'
        title={
          <Text weight='bold' size='medium' as='span'>
            {t('See how your phone settings have changed over time')}
          </Text>
        }
        cardStyles={{ width: '340px' }}
        containerStyles={{ overflow: 'visible' }}
        description={
          <Text weight='regular' size='medium' as='span'>
            {t(
              'With Phone System History, see how and when your phone system settings have changed. We will be releasing history related to more settings in the near future.'
            )}
          </Text>
        }
        actions={
          <Button
            variant='tertiary'
            color='primary'
            onClick={handleGotItClick}
            trackingId={trackingIdPrefix ? `${trackingIdPrefix}-see-history-callout-got-it-button` : undefined}
          >
            {t('Got it')}
          </Button>
        }
      />
    </div>
  );
};
